import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
  useIncidents, useCreateIncident, usePatchIncident, useDeleteIncident, useSeedIncident,
  type Incident,
} from '../api';
import GoKit from '../components/GoKit';
import RelativeTime, { DAY_SCALE } from '../components/RelativeTime';

export default function Incidents() {
  const { data, isLoading, error } = useIncidents();
  const [adding, setAdding] = useState(false);

  if (isLoading) return <p className="text-slate-500 dark:text-slate-400">Loading…</p>;
  if (error) return <p className="text-red-600 dark:text-red-400">Error loading incidents.</p>;

  const incidents = data?.incidents ?? [];
  const active = incidents.filter((i) => i.status === 'active');
  const planning = incidents.filter((i) => i.status === 'planning');
  const closed = incidents.filter((i) => i.status === 'closed');

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Incident command</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Neighborhood accountability. Seed an incident from the ward roster, then work the board.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PreparednessPacketButton />
          <button
            onClick={() => setAdding((v) => !v)}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            {adding ? 'Cancel' : 'New incident'}
          </button>
        </div>
      </header>

      {adding && <NewIncidentForm onDone={() => setAdding(false)} />}

      {incidents.length === 0 && !adding && (
        <EmptyState onStart={() => setAdding(true)} />
      )}

      <Section title="Active" incidents={active} />
      <Section title="Preparedness / planning" incidents={planning} />
      <Section title="Closed" incidents={closed} />

      {/* The go-kit belongs here rather than inside a packet: it is a standing
          list the ward maintains between emergencies, and the moment you need
          it is the moment you are least able to produce it from memory. Ticks
          go against the active incident when there is one. */}
      <section>
        <h2 className="mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Go-kit
        </h2>
        <GoKit incidentId={active[0]?.id} />
      </section>
    </div>
  );
}

function EmptyState({ onStart }: { onStart: () => void }) {
  return (
    <div className="rounded border border-dashed border-slate-300 dark:border-slate-700 p-6 text-sm text-slate-600 dark:text-slate-400">
      <p className="font-medium text-slate-800 dark:text-slate-200">Nothing set up yet.</p>
      <p className="mt-1 max-w-2xl">
        The useful first move is a drill: create one, seed it from the ward roster, fill in
        emergency contacts and medical needs as you learn them, and print the packet for your
        go-bag. The next incident carries all of that forward automatically.
      </p>
      <div className="mt-3 flex gap-2">
        <PreparednessPacketButton />
        <button
          onClick={onStart}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-sm"
        >
          Create a drill instead
        </button>
      </div>
    </div>
  );
}

function Section({ title, incidents }: { title: string; incidents: Incident[] }) {
  if (incidents.length === 0) return null;
  return (
    <section>
      <h2 className="mb-1 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h2>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800 rounded border border-slate-200 dark:border-slate-700">
        {incidents.map((i) => <Row key={i.id} incident={i} />)}
      </ul>
    </section>
  );
}

function Row({ incident: i }: { incident: Incident }) {
  const patch = usePatchIncident();
  const del = useDeleteIncident();

  return (
    <li className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Link to={`/incidents/${i.id}`} className="font-medium text-slate-900 dark:text-slate-100 hover:underline">
            {i.name}
          </Link>
          {i.is_drill && (
            <span className="rounded bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
              Drill
            </span>
          )}
          {i.status === 'active' && (
            <span className="rounded bg-rose-600 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-white">
              Active
            </span>
          )}
          {i.kind && <span className="text-xs text-slate-500 dark:text-slate-400">{i.kind}</span>}
        </div>
        <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <span className="tabular-nums">{i.site_count ?? 0} addresses · {i.person_count ?? 0} people</span>
          {i.seeded_at
            ? <span>seeded <RelativeTime at={i.seeded_at} thresholds={DAY_SCALE} /></span>
            : <span className="text-amber-700 dark:text-amber-400">not seeded</span>}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 text-xs">
        {i.status !== 'active' && (
          <button
            onClick={() => patch.mutate({ id: i.id, body: { status: 'active' } })}
            disabled={patch.isPending}
            className="rounded border border-rose-300 dark:border-rose-700 px-2 py-0.5 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950"
          >
            Activate
          </button>
        )}
        {i.status === 'active' && (
          <button
            onClick={() => patch.mutate({ id: i.id, body: { status: 'closed' } })}
            disabled={patch.isPending}
            className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            Close
          </button>
        )}
        <Link
          to={`/incidents/${i.id}`}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          Board
        </Link>
        <button
          onClick={() => {
            // Hard delete — incidents hold medical detail for non-members, so a
            // closed drill is meant to be purgeable. Spell that out.
            const msg = `Permanently delete "${i.name}"?\n\n`
              + `This purges ${i.site_count ?? 0} addresses, ${i.person_count ?? 0} people, `
              + `and all contacts, log entries, and tasks. It cannot be undone.`;
            if (window.confirm(msg)) del.mutate(i.id);
          }}
          disabled={del.isPending}
          className="rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 text-slate-500 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-950 dark:hover:text-rose-300"
        >
          Delete
        </button>
      </div>
      {(patch.error || del.error) && (
        <span className="text-xs text-rose-600 dark:text-rose-400">
          {((patch.error || del.error) as Error).message}
        </span>
      )}
    </li>
  );
}

function NewIncidentForm({ onDone }: { onDone: () => void }) {
  const create = useCreateIncident();
  const [name, setName] = useState('');
  const [kind, setKind] = useState('');
  const [isDrill, setIsDrill] = useState(true);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        create.mutate(
          { name: name.trim(), kind: kind.trim() || null, is_drill: isDrill },
          { onSuccess: onDone },
        );
      }}
      className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-2"
    >
      <div className="flex flex-wrap gap-2">
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name — e.g. Wasatch Drill 2026"
          className="flex-1 min-w-56 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        />
        <input
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          placeholder="Kind — earthquake, windstorm…"
          className="w-56 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
        />
        <label className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300">
          <input type="checkbox" checked={isDrill} onChange={(e) => setIsDrill(e.target.checked)} />
          Drill
        </label>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Drills are banner-marked everywhere, including on every printed page, so exercise numbers
        are never mistaken for real ones.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={create.isPending || !name.trim()}
          className="rounded bg-slate-800 dark:bg-slate-200 px-3 py-1 text-sm text-white dark:text-slate-900 disabled:opacity-50"
        >
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
        <button type="button" onClick={onDone} className="text-sm text-slate-500 dark:text-slate-400">
          Cancel
        </button>
      </div>
      {create.error && (
        <p className="rounded bg-rose-50 dark:bg-rose-950 px-2 py-1 text-xs text-rose-700 dark:text-rose-300">
          Create failed: {(create.error as Error).message}
        </p>
      )}
    </form>
  );
}

/**
 * One click from nothing to a printable packet.
 *
 * Printing cold — before anything happens — is the whole point of the packet,
 * but it previously meant creating an incident, seeding it, then finding the
 * packet route. This does all three.
 *
 * It still creates an incident, because the packet has to be printed FROM
 * something: the snapshot is what makes the paper reproducible later ("what
 * did the packet in my go-bag say?") and what lets you re-print an identical
 * copy. It is created in `planning` status, so it never counts as active and
 * never competes with a real response.
 */
function PreparednessPacketButton() {
  const create = useCreateIncident();
  const seed = useSeedIncident();
  const navigate = useNavigate();
  const [step, setStep] = useState<string | null>(null);

  const go = () => {
    setStep('Creating…');
    const today = new Date().toISOString().slice(0, 10);
    create.mutate(
      { name: `Preparedness packet ${today}`, kind: 'preparedness', is_drill: true },
      {
        onSuccess: (c) => {
          setStep('Loading the roster…');
          seed.mutate({ id: c.id, carry_forward_from: 'latest' }, {
            onSuccess: () => navigate(`/incidents/${c.id}/packet`),
            onError: () => setStep(null),
          });
        },
        onError: () => setStep(null),
      },
    );
  };

  return (
    <span className="flex items-center gap-2">
      <button
        onClick={go}
        disabled={create.isPending || seed.isPending}
        className="rounded bg-slate-800 dark:bg-slate-200 px-3 py-1 text-sm text-white dark:text-slate-900 disabled:opacity-50"
        title="Build a printable packet from the current roster — sector sheets, household cards, map, go-kit, and blank log forms"
      >
        {step ?? 'Preparedness packet'}
      </button>
      {(create.error || seed.error) && (
        <span className="text-xs text-rose-600 dark:text-rose-400">
          {((create.error || seed.error) as Error).message}
        </span>
      )}
    </span>
  );
}
